import { Tool, QuestionnaireData, BusinessGoal, Feature, TeamSize } from '../../types';
import { WEIGHTS } from './constants';

const GOAL_LABELS: Record<BusinessGoal, string> = {
  'increase-sales': 'increase sales',
  'improve-marketing': 'improve marketing',
  'enhance-customer-service': 'enhance customer service',
  'streamline-operations': 'streamline operations',
  'reduce-costs': 'reduce costs',
  'build-brand': 'build your brand',
  'expand-customer-base': 'expand your customer base',
  'automate-tasks': 'automate tasks',
  'manage-scheduling': 'manage scheduling',
  'track-jobs': 'track jobs',
  'security': 'improve security',
  'scalability': 'scale your business'
};

const FEATURE_LABELS: Record<Feature, string> = {
  'ease-of-use': 'ease of use',
  'integrations': 'third-party integrations',
  'automation': 'workflow automation',
  'mobile': 'mobile access',
  'support': 'customer support',
  'security': 'security features',
  'scalability': 'scalability',
  'scheduling': 'scheduling',
  'invoicing': 'invoicing',
  'job-tracking': 'job tracking',
  'field-service': 'field service management',
  'budget-tracking': 'budget tracking',
  'change-order-management': 'change order management',
  'document-management': 'document management',
  'project-management': 'project management',
  'timesheet-management': 'timesheet management',
  'collaboration': 'team collaboration',
  'time-tracking': 'time tracking',
  'analytics': 'analytics',
  'task-management': 'task management',
  'self-hosted': 'self-hosted deployment',
  'api': 'API access'
};

const TEAM_SIZE_LABELS: Record<TeamSize, string> = {
  'solo': 'solo operators',
  'small': 'small teams (2-5 people)',
  'medium': 'medium teams (6-20 people)',
  'large': 'larger teams (20+ people)'
};

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  'startup': 'startups',
  'small-business': 'small businesses',
  'freelancer': 'freelancers',
  'side-hustler': 'side businesses',
  'trades': 'trades businesses',
  'agency': 'agencies',
  'content-creator': 'content creators',
  'solopreneur': 'solopreneurs'
};

const INDUSTRY_LABELS: Record<string, string> = {
  'technology': 'technology',
  'retail': 'retail',
  'healthcare': 'healthcare',
  'education': 'education',
  'finance': 'finance',
  'creative': 'creative industries',
  'marketing': 'marketing',
  'consulting': 'consulting',
  'trades': 'trades',
  'home-services': 'home services',
  'real-estate': 'real estate',
  'other': 'your industry'
};

const STAGE_LABELS: Record<string, string> = {
  'idea': 'early-stage businesses',
  'launching': 'businesses just launching',
  'growing': 'growing businesses',
  'scaling': 'scaling businesses'
};

export interface ReasonContext {
  matchedGoals: BusinessGoal[];
  matchedFeatures: Feature[];
  businessTypeMatch: boolean;
  subtypeMatch: boolean;
  industryMatch: boolean;
  budgetMatch: boolean;
  technicalMatch: boolean;
  stageMatch: boolean;
  isEssential: boolean;
  isPrioritized: boolean;
  hasIntegrations: boolean;
  isEasyToUse: boolean;
}

function formatList(items: string[], maxItems: number = 2): string {
  const limited = items.slice(0, maxItems);
  if (limited.length === 0) return '';
  if (limited.length === 1) return limited[0];
  if (limited.length === 2) return `${limited[0]} and ${limited[1]}`;
  return `${limited.slice(0, -1).join(', ')}, and ${limited[limited.length - 1]}`;
}

export function generatePersonalizedReasons(
  tool: Tool,
  data: Partial<QuestionnaireData>,
  context: ReasonContext
): string[] {
  const reasons: string[] = [];
  const businessType = data.businessType || 'small-business';
  const industry = data.industry || 'other';
  const teamSize = data.teamSize || 'small';
  const stage = data.businessStage || 'growing';

  if (context.businessTypeMatch && context.industryMatch) {
    const btLabel = BUSINESS_TYPE_LABELS[businessType] || businessType.replace(/-/g, ' ');
    const indLabel = INDUSTRY_LABELS[industry] || industry.replace(/-/g, ' ');
    reasons.push(`Built for ${btLabel} in ${indLabel}`);
  } else if (context.businessTypeMatch) {
    const btLabel = BUSINESS_TYPE_LABELS[businessType] || businessType.replace(/-/g, ' ');
    const sizeLabel = TEAM_SIZE_LABELS[teamSize];
    reasons.push(`Designed for ${btLabel} with ${sizeLabel}`);
  } else if (context.industryMatch) {
    const indLabel = INDUSTRY_LABELS[industry] || industry.replace(/-/g, ' ');
    reasons.push(`Tailored for the ${indLabel} industry`);
  }

  if (context.matchedGoals.length > 0) {
    const goalLabels = context.matchedGoals.map(g => GOAL_LABELS[g] || g.replace(/-/g, ' '));
    const goalsText = formatList(goalLabels, 2);
    reasons.push(`Helps you ${goalsText}`);
  }

  if (context.matchedFeatures.length > 0) {
    const featureLabels = context.matchedFeatures.map(f => FEATURE_LABELS[f] || f.replace(/-/g, ' '));
    const featuresText = formatList(featureLabels, 2);
    reasons.push(`Includes ${featuresText}`);
  }

  if (context.isEssential && reasons.length < 3) {
    reasons.push(`Core tool for ${BUSINESS_TYPE_LABELS[businessType] || 'your business'}`);
  }

  if (context.stageMatch && reasons.length < 3) {
    const stageLabel = STAGE_LABELS[stage] || stage;
    reasons.push(`Perfect for ${stageLabel}`);
  }

  if (context.budgetMatch && reasons.length < 3) {
    const budgetLabels: Record<string, string> = {
      'free': 'free to use',
      'low': 'budget-friendly',
      'medium': 'competitively priced',
      'high': 'enterprise-grade pricing'
    };
    const budgetLabel = budgetLabels[data.budget || 'medium'] || 'fits your budget';
    reasons.push(`${budgetLabel.charAt(0).toUpperCase() + budgetLabel.slice(1)} for your needs`);
  }

  if (context.technicalMatch && data.technicalExpertise === 'beginner' && reasons.length < 3) {
    reasons.push('Easy to learn with no technical expertise required');
  } else if (context.technicalMatch && data.technicalExpertise === 'advanced' && reasons.length < 3) {
    reasons.push('Advanced features for technical users');
  }

  if (context.hasIntegrations && reasons.length < 3) {
    reasons.push('Connects with your existing tools');
  }

  if (context.isEasyToUse && data.technicalExpertise === 'beginner' && reasons.length < 3) {
    reasons.push('User-friendly interface, quick to set up');
  }

  if (reasons.length === 0) {
    const btLabel = BUSINESS_TYPE_LABELS[businessType] || 'your business';
    reasons.push(`Recommended ${tool.category.toLowerCase()} solution for ${btLabel}`);
  }

  return reasons.slice(0, 3);
}

export function calculateMatchScore(
  _tool: Tool,
  data: Partial<QuestionnaireData>,
  rawScore: number
): number {
  const baseScore = 15;
  const maxPriority = 75;
  const maxEssential = WEIGHTS.ESSENTIAL_CATEGORY;
  const maxBusinessType = WEIGHTS.BUSINESS_TYPE;
  const maxIndustry = WEIGHTS.INDUSTRY;

  const goalCount = data.goals?.length || 0;
  const maxGoals = Math.min(goalCount, 2) * (WEIGHTS.GOALS / 2)
    + Math.max(0, goalCount - 2) * 20;

  const featureCount = data.features?.length || 0;
  const maxFeatures = Math.min(featureCount, 2) * (WEIGHTS.FEATURES / 2)
    + Math.max(0, featureCount - 2) * 20;

  const maxBudget = WEIGHTS.BUDGET;
  const maxTechnical = WEIGHTS.TECHNICAL_MATCH;
  const maxStage = WEIGHTS.STAGE;
  const maxContext = 55;
  const maxTeamSize = 25;
  const maxIntegration = 25;
  const maxEaseOfUse = data.technicalExpertise === 'beginner' ? 15 : 0;

  const maxPossibleScore = baseScore + maxPriority + maxEssential
    + maxBusinessType + maxIndustry + maxGoals + maxFeatures
    + maxBudget + maxTechnical + maxStage + maxContext + maxTeamSize
    + maxIntegration + maxEaseOfUse;

  const clampedScore = Math.max(0, rawScore);
  const percentage = Math.min(100, Math.round((clampedScore / maxPossibleScore) * 100));
  return Math.max(5, percentage);
}
